import React, { useCallback, useEffect } from 'react';

import { Box } from '@mui/material';

import Heading from '@/features/Dashboard/components/Heading';
import AddAppHeader from '@/features/Dashboard/drawer/components/AddAppHeader';
import { useDrawerSteps } from '@/features/Dashboard/drawer/context/DrawerStepsContext';
import { DrawerBody } from '@/features/Dashboard/drawer/DrawerBody';
import ColorPaletteSelector from '@/features/Dashboard/form/AppForm/components/ColorPaletteSelector';
import { FormPreview } from '@/features/Dashboard/form/AppForm/components/FormPreview';
import { ItemSelection } from '@/features/Dashboard/form/AppForm/components/ItemSelection';
import { SelectChartType } from '@/features/Dashboard/form/AppForm/components/SelectChartType';
import { TabName } from '@/features/Dashboard/form/AppForm/components/TabName';
import { useAppForm } from '@/features/Dashboard/form/AppForm/context';
import { getAppTemplateFromFormValues } from '@/features/Dashboard/form/AppForm/utils/getAppTemplateFromFormValues';
import { AppType } from '@/features/Dashboard/types';

const Content = ({ type }: { type: AppType | undefined }) => {
  switch (type) {
    case AppType.Table:
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Heading
            subtitle='Set up your table preferences and data'
            title='Configure data table'
          />
          <TabName />
          <ItemSelection />
        </Box>
      );
    case AppType.Tiles:
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Heading
            subtitle='Set up your preferences and data'
            title='Configure highlighted price'
          />
          <ItemSelection />
        </Box>
      );
    case AppType.Seasonal:
    case AppType.Spot:
    case AppType.FWD:
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Heading
            subtitle='Set up your chart preferences and data'
            title={`Configure ${type === AppType.Spot || type === AppType.Seasonal ? 'spot' : 'forward curves'} chart`}
          />
          <TabName />
          {(type === AppType.Spot || type === AppType.Seasonal) && <SelectChartType />}
          <ItemSelection />
          {type !== AppType.Seasonal && <ColorPaletteSelector />}
        </Box>
      );

    default:
      return null;
  }
};

// Helper component to sync form dirty state to the drawer without setting state during render
const HasChangesSync = ({
  isDirty,
  setHasChanges
}: {
  isDirty: boolean;
  setHasChanges: (v: boolean) => void;
}) => {
  useEffect(() => {
    setHasChanges(isDirty);
  }, [isDirty, setHasChanges]);
  return null;
};

export const AppForm = () => {
  const { drawerStep, setHasChanges } = useDrawerSteps();
  const { form } = useAppForm();

  const createWidget = useCallback(() => {
    const values = form.state.values;
    if (!values) return;
    return getAppTemplateFromFormValues(values);
  }, [drawerStep, form]);

  return (
    <>
      <form.Subscribe selector={s => s.isValid}>
        {isValid => (
          <AddAppHeader
            createWidget={createWidget}
            isValid={isValid}
          />
        )}
      </form.Subscribe>

      <DrawerBody>
        <form.Subscribe selector={s => s.values?.type}>
          {type => <Content type={type} />}
        </form.Subscribe>
        <Box sx={{ mt: 3 }}>
          <FormPreview />
        </Box>

        {/* Sync the drawer's hasChanges flag with the form's dirty state */}
        <form.Subscribe selector={s => s.isDirty}>
          {isDirty => (
            <HasChangesSync
              isDirty={!!isDirty}
              setHasChanges={setHasChanges}
            />
          )}
        </form.Subscribe>
      </DrawerBody>
    </>
  );
};
